import { useUiLanguage } from "@/i18n/useUiLanguage";
import { tr, uiLocale } from "@/i18n/copy";
import { useEffect, useMemo, useState } from "react";
import { ChevronDown, Sparkles, UserRound } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import AuthenticatedApiImage from "@/components/AuthenticatedApiImage";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const priceLabel = (from, to, currency = "EUR") => {
  const label = currency === "EUR" ? "€" : currency;
  if (from == null && to == null) return "";
  if (from != null && to != null) return `${label} ${Number(from).toFixed(0)} – ${label} ${Number(to).toFixed(0)}`;
  if (from != null) return tr("da {{amount}}", {amount: `${label} ${Number(from).toFixed(0)}`});
  return tr("fino a {{amount}}", {amount: `${label} ${Number(to).toFixed(0)}`});
};
const durationLabel = (minutes) => minutes ? tr("circa {{hours}} h", {hours: new Intl.NumberFormat(uiLocale(), {maximumFractionDigits:1}).format(Number(minutes)/60)}) : "";

export default function ClientProjectProposals({ relationshipId, onContinue, onChanged, currency = "EUR" }) {
  useUiLanguage();
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState("");
  const load = () => api.get(`/client/relationships/${relationshipId}/project-proposals`).then(setItems).catch(()=>setItems([]));
  useEffect(() => {
    if (!relationshipId) return;
    load();
    const refresh = () => load();
    window.addEventListener("tps:data-changed", refresh);
    return () => window.removeEventListener("tps:data-changed", refresh);
  }, [relationshipId]);
  const pending = useMemo(() => items.filter((item)=>item.status==="presented"), [items]);
  if (!items.length) return null;

  const openQuotation = (proposal) => {
    const url = new URL(window.location.href);
    url.searchParams.set("view", "quotations");
    url.searchParams.set("quotation", proposal.quotation_id);
    window.location.assign(url.toString());
  };
  const decide = async (proposal, action) => {
    setBusy(proposal.id);
    try {
      const updated = await api.post(`/client/relationships/${relationshipId}/project-proposals/${proposal.id}/${action}`);
      toast.success(action==="accept" ? tr("Proposta accettata") : tr("Proposta rifiutata"));
      await load();
      onChanged?.();
      window.dispatchEvent(new CustomEvent("tps:data-changed",{detail:{source:"client-project-proposal",id:proposal.id,action}}));
      if (action==="accept") onContinue?.(updated);
    } catch(e) { toast.error(errMsg(e)); }
    finally { setBusy(""); }
  };

  return <details className="group rounded-3xl border border-white/10 bg-card/85 p-5 shadow-xl">
    <summary className="flex cursor-pointer list-none items-center justify-between gap-3">
      <div className="flex items-center gap-3"><span className="grid h-11 w-11 place-items-center rounded-2xl bg-primary/10"><Sparkles className="h-5 w-5 text-primary"/></span><div><p className="font-semibold">{tr("Proposte progetto")}</p><p className="mt-1 text-xs text-muted-foreground">{pending.length ? tr("{{0}} da valutare", {"0": pending.length}) : tr("Storico delle proposte ricevute")}</p></div></div>
      <ChevronDown className="h-5 w-5 text-muted-foreground transition-transform group-open:rotate-180"/>
    </summary>
    <div className="mt-4 space-y-3">
      {items.map((proposal)=><article key={proposal.id} className="rounded-2xl border bg-background/40 p-4">
        <div className="flex items-start justify-between gap-3"><div className="min-w-0"><div className="flex items-center gap-2"><UserRound className="h-4 w-4 text-primary"/><b>{proposal.artist?.name || tr("Tattoo Artist")}</b></div><h3 className="mt-2 text-lg font-semibold">{proposal.title || tr("Proposta Tattoo")}</h3></div><span className="rounded-full border px-2.5 py-1 text-[10px] font-bold">{proposal.status==="presented"?tr("DA VALUTARE"):proposal.status==="accepted"?tr("ACCETTATA"):proposal.status==="expired"?tr("SCADUTA"):proposal.status==="cancelled"?tr("ANNULLATA"):tr("RIFIUTATA")}</span></div>
        {proposal.has_image && <AuthenticatedApiImage path={`/client/relationships/${relationshipId}/project-proposals/${proposal.id}/image`} alt={proposal.title||tr("Proposta Tattoo")} className="mt-3 max-h-[560px] w-full rounded-2xl border bg-black/20 object-contain" fallback={<div className="mt-3 rounded-2xl border p-6 text-center text-xs text-muted-foreground">{tr("Caricamento anteprima protetta…")}</div>}/>}
        {proposal.description && <p className="mt-3 whitespace-pre-wrap text-sm leading-6 text-muted-foreground">{proposal.description}</p>}
        <div className="mt-3 flex flex-wrap gap-2 text-xs">
          {priceLabel(proposal.price_from,proposal.price_to,currency) && <span className="rounded-full border px-2.5 py-1">{priceLabel(proposal.price_from,proposal.price_to,currency)}</span>}
          {durationLabel(proposal.duration_hint_minutes) && <span className="rounded-full border px-2.5 py-1">{durationLabel(proposal.duration_hint_minutes)}</span>}
          {proposal.requires_consultation && <span className="rounded-full border border-primary/30 bg-primary/5 px-2.5 py-1 text-primary">{tr("Previa consulenza")}</span>}
        </div>
        {proposal.quotation_id && <Button className="mt-4" onClick={()=>openQuotation(proposal)}>{tr("Apri proposta e preventivo")}</Button>}
        {proposal.status==="presented" && !proposal.quotation_id && <div className="mt-4 flex flex-wrap gap-2"><Button disabled={busy===proposal.id} onClick={()=>decide(proposal,"accept")}>{tr("Accetta proposta")}</Button><Button variant="outline" disabled={busy===proposal.id} onClick={()=>decide(proposal,"reject")}>{tr("Rifiuta")}</Button></div>}
        {proposal.status==="accepted" && !proposal.quotation_id && <Button variant="outline" className="mt-4" onClick={()=>onContinue?.(proposal)}>{proposal.requires_consultation?tr("Prenota la consulenza"):tr("Richiedi l'appuntamento")}</Button>}
      </article>)}
    </div>
  </details>;
}


